<template>
  <!-- Retry Mode: Show simplified retry UI -->
  <OrderPageRetry
    v-if="isRetryMode"
    :paymentMethodsOptions="{
      sepa: { cssClass: 'field-icon fa-solid fa-building-columns' },
      bank_card: { cssClass: 'field-icon fa-solid fa-credit-card' },
      iframeStyle: 'orderPage',
    }"
  />

  <!-- Normal Mode: Show product listing -->
  <template v-else-if="showListing()">
    <div v-if="showOfferListing">
      <div v-if="title && extrasellItems.length" class="title">{{ title }}</div>
      <productlist
        categoryid="orderPage"
        :mode="mode"
        :count="count"
        :showaddtocart="true"
        :addToCartLabel="addToCartLabel"
        :postMountedProducts="extrasellItems"
        :cartMode="true"
      />
    </div>
    <seo pagetype="order-page" />
    <tracking />
    <loader />
    <debug />
  </template>
</template>
<script>
import CartService from "@/services/cart";
import AbTests from "@/services/abTests";
import PaymentMethodService from "@/services/payment/paymentMethodService";
import productlist from "@/components/productlist/ProductList.vue";
import tracking from "@/components/Tracking.vue";
import seo from "@/components/Seo.vue";
import loader from "@/components/Loader.vue";
import { useCartStore } from "@/store/cart";
import { useUpselledStore } from "@/store/upselled";
import { useDraftStore } from "@/store/draft";
import Debug from "@/components/Debug.vue";
import OrderPageRetry from "@/components/checkout/OrderPageRetry.vue";
import AlreadyOrderedProducts from "@/services/alreadyOrderedProducts";
import OrderPageOffers from "@/services/orderPageOffers";
import { showOrderPageErrorModalsFromQuery } from "@/services/orderPageErrorModal";
import {
  isOrderPagePaymentRetryMode,
  stripInvalidRetryFromUrl,
} from "@/services/orderPageRetryParam";

export default {
  name: "OrderPageProcess",
  components: {
    Debug,
    productlist,
    tracking,
    seo,
    loader,
    OrderPageRetry,
  },
  props: {
    mode: {
      type: String,
      default: "orderpage-list",
    },
    count: {
      type: Number,
      default: 1,
    },
    addToCartLabel: {
      type: String,
    },
  },
  data() {
    return {
      cart: null,
      productDetails: null,
      notCartFilteredExtrasellItems: [],
      extrasellItems: [],
      hideListing: false,
      alreadyOrderedProductsSalesforceId: [],
      blockedChoiceIds: [],
      alreadyOrderedTick: 0,
    };
  },
  computed: {
    isRetryMode() {
      return isOrderPagePaymentRetryMode(
        new URLSearchParams(window.location.search),
      );
    },
    lastCartUpdate() {
      return useCartStore().lastUpdate;
    },
    selectedPaymentMethod() {
      return useDraftStore().selectedPaymentMethod;
    },
    title() {
      if (this.productDetails?.limitCartTo1Item) {
        return this.$t("labels.chooseYourOffer");
      }

      if (this.cart?.items?.length > 0) {
        return this.extrasellItems.length === 1
          ? this.$t("labels.additionalOffer")
          : this.$t("labels.additionalOffers");
      }

      return this.$t("labels.chooseYourOffers");
    },
    showOfferListing() {
      void this.alreadyOrderedTick;
      return !OrderPageOffers.shouldHideOfferListing(
        this.notCartFilteredExtrasellItems,
        this.productDetails,
        this.offerListingOptions(),
      );
    },
  },
  watch: {
    lastCartUpdate: {
      handler(newValue, oldValue) {
        if (!window?.globalConfig?.domainId) {
          return;
        }

        this.cart = this.getCart();
      },
    },
    selectedPaymentMethod: {
      handler(newValue, oldValue) {
        this.filterExtrasellItems();
      },
    },
    cart: {
      handler(newValue, oldValue) {
        this.filterExtrasellItems();
      },
      deep: true,
    },
  },
  mounted() {
    // reset cart each time we land on the order page
    useCartStore().updateCart({ items: [] });
    this.cart = this.getCart();
    this.productDetails = window.productDetails;

    const urlParams = new URLSearchParams(window.location.search);
    const cleanedUrl = stripInvalidRetryFromUrl();
    if (cleanedUrl !== window.location.href) {
      window.history.replaceState({}, "", cleanedUrl);
      urlParams.delete("retry");
    }
    this._syncExtrasellPageLayoutClass(urlParams);
    showOrderPageErrorModalsFromQuery();

    // Check if this is a retry mode - if so, skip automatic cart population
    // PaymentForm.vue will handle rebuilding the cart from orderHistory
    if (isOrderPagePaymentRetryMode(urlParams)) {
      console.log(
        "Payment retry mode detected in OrderPageProcess, skipping automatic cart population",
      );
      this.filterExtrasellItems();
      return;
    }

    if (urlParams.has("hideListing")) {
      console.log("hideListing", urlParams.get("hideListing"));
      this.hideListing = urlParams.get("hideListing") == "true";
      useCartStore().cart.hideDelete = urlParams.get("hideListing") == "true";
    } else {
      useCartStore().cart.hideDelete = false; // hide delete button on cart items
    }

    const abGroups = this.productDetails.initialChoices;

    // Radio mode + single AB group: the pre-selected offer is switched by
    // picking another card, not by removing from the summary. Cart mode must
    // keep delete enabled so users can empty the basket line by line.
    if (abGroups.length === 1 && this.productDetails?.limitCartTo1Item) {
      useCartStore().cart.hideDelete = true;
    }

    this.notCartFilteredExtrasellItems = [];
    let selectedChoices = [];

    for (const abGroup of abGroups) {
      const selectedAbChoice = AbTests.electItemOfGroup(abGroup);
      if (
        urlParams.has("addChoices") &&
        urlParams.get("addChoices") !== "true"
      ) {
        const choicesToAdd = urlParams.get("addChoices").split(",");
        for (const choiceToAddId of choicesToAdd) {
          let selectedChoice = this.productDetails.choices.find(
            (choice) => choice.id === choiceToAddId,
          );
          const productToAdd = this.productDetails;
          productToAdd.choice = selectedChoice;
          productToAdd.qtyIncr = 1;
          CartService.addToCart(productToAdd);
        }
      }
      if (selectedAbChoice) {
        let selectedChoice = this.productDetails.choices.find(
          (choice) => choice.id === selectedAbChoice.id,
        );

        selectedChoices.push(selectedChoice);
      }
    }

    if (selectedChoices.length) {
      for (const selectedChoice of selectedChoices) {
        if (!selectedChoice) {
          continue;
        }
        const productToAdd = Object.assign({}, this.productDetails);
        productToAdd.qtyIncr = 1;
        // Clone so we do not mutate Magellan's `default` flag on the shared catalog.
        productToAdd.choice = Object.assign({}, selectedChoice, { show: true });
        productToAdd.choices = [productToAdd.choice];
        this.notCartFilteredExtrasellItems.push(productToAdd);
      }
    }

    this.filterExtrasellItems();
    this.syncAlreadyOrderedProducts();
    window.addEventListener(
      "already-ordered-updated",
      this.onAlreadyOrderedUpdated,
    );

    this.maybeAutoSelectSingleEligibleOffer();
  },
  unmounted() {
    window.removeEventListener(
      "already-ordered-updated",
      this.onAlreadyOrderedUpdated,
    );
    document.documentElement.classList.remove("vg-page-extrasell");
  },
  methods: {
    _syncExtrasellPageLayoutClass(urlParams) {
      const isFunnelExtrasellPage =
        urlParams.get("hideListing") === "true" ||
        (urlParams.has("addChoices") && urlParams.get("addChoices") !== "true");
      document.documentElement.classList.toggle(
        "vg-page-extrasell",
        isFunnelExtrasellPage,
      );
    },
    syncAlreadyOrderedProducts() {
      this.alreadyOrderedProductsSalesforceId = (
        AlreadyOrderedProducts.getAlreadyOrderedProducts() || []
      ).map((id) => String(id));
    },
    onAlreadyOrderedUpdated(event) {
      const productsSalesforceId = event?.detail?.productsSalesforceId;
      const blockedChoiceIds = event?.detail?.blockedChoiceIds;
      this.alreadyOrderedProductsSalesforceId = productsSalesforceId?.length
        ? productsSalesforceId.map((id) => String(id))
        : (AlreadyOrderedProducts.getAlreadyOrderedProducts() || []).map((id) =>
            String(id),
          );
      this.blockedChoiceIds = blockedChoiceIds?.length
        ? blockedChoiceIds.map((id) => String(id))
        : [];
      this.alreadyOrderedTick += 1;
      this.maybeAutoSelectSingleEligibleOffer();
    },
    offerListingOptions() {
      return {
        getDefaultChoice: (product) => product?.choice,
        isChoiceDisabled: (product) => this.isChoiceDisabled(product),
      };
    },
    maybeAutoSelectSingleEligibleOffer() {
      const cart = this.getCart();
      if (cart?.items?.length) {
        return;
      }

      const eligible = OrderPageOffers.getEligibleOffers(
        this.notCartFilteredExtrasellItems,
        this.offerListingOptions(),
      );
      if (eligible.length !== 1) {
        return;
      }

      const singleItem = Object.assign({}, eligible[0]);
      singleItem.qtyIncr = 1;
      CartService.addToCart(singleItem);
    },
    isChoiceDisabled(product) {
      const choice = product?.choice;
      const choiceId = choice?.id ? String(choice.id) : null;

      if (this.blockedChoiceIds.length) {
        return !!choiceId && this.blockedChoiceIds.includes(choiceId);
      }

      const productSalesforceId = choice?.salesforceProductId
        ? String(choice.salesforceProductId)
        : null;

      if (!productSalesforceId) {
        return false;
      }

      return this.alreadyOrderedProductsSalesforceId.includes(
        productSalesforceId,
      );
    },
    getCart() {
      return useCartStore().cart;
    },
    getDefaultPaymentMethod() {
      return PaymentMethodService.getDefaultPaymentMethod();
    },
    filterExtrasellItems() {
      const alreadyInCartIds = [];
      for (const cartItem of this.cart.items) {
        alreadyInCartIds.push(cartItem.choice.id);
      }

      const inCartParents = [];
      if (this.productDetails.upsells) {
        const upsellParentMapping = {};
        const pageUpsellChoiceIds = new Set();
        for (const upsellItem of this.productDetails.upsells) {
          for (const upsellGroup of upsellItem.children) {
            for (const upsellChoice of upsellGroup) {
              if (upsellItem.scope === "page") {
                pageUpsellChoiceIds.add(String(upsellChoice.id));
              } else if (!upsellParentMapping[upsellChoice.id]) {
                upsellParentMapping[upsellChoice.id] = [upsellItem.parentId];
              } else {
                upsellParentMapping[upsellChoice.id].push(upsellItem.parentId);
              }
            }
          }
        }
        for (const alreadyInCartId of alreadyInCartIds) {
          const upsellParents = upsellParentMapping[alreadyInCartId];
          if (upsellParents) {
            inCartParents.push(...upsellParents);
          }
        }
        // Vente flash de la page : le parent est l'offre remplacee.
        for (const cartItem of this.cart.items) {
          if (!pageUpsellChoiceIds.has(String(cartItem.choice.id))) {
            continue;
          }
          const downgrade = useUpselledStore().getDowngradeChoice(
            cartItem.choice,
          );
          if (downgrade?.id) {
            inCartParents.push(downgrade.id);
          }
        }
      }

      this.extrasellItems = this.notCartFilteredExtrasellItems.filter(
        (item) => {
          const notAlreadyInCart = !alreadyInCartIds.includes(item.choice.id);
          const choiceHasNotBeenUpselled = !inCartParents.includes(
            item.choice.id,
          );
          return (
            (!this.productDetails.limitCartTo1Item &&
              notAlreadyInCart &&
              choiceHasNotBeenUpselled) ||
            this.productDetails.limitCartTo1Item
          );
        },
      );
    },
    showListing() {
      return !this.hideListing;
    },
  },
};
</script>
